import { Radar, Clock, CalendarCheck2, Gauge, Eye, Timer } from "lucide-react";
import { timeAgo, fmtAbs, latencySeconds, fmtDuration, latencyTone } from "@/lib/time";

// What the "event time" (provenance.observed_at) means for each hazard.
const EVENT_META = {
  occurred: { label: "Occurred", Icon: CalendarCheck2, color: "text-atlas-orange" },
  measured: { label: "Measured", Icon: Gauge, color: "text-atlas-cyan" },
  watch: { label: "No confirmed event yet", Icon: Eye, color: "text-amber-300" },
};

const eventMeta = (obs) => EVENT_META[obs?.event_status] || EVENT_META.measured;

// Compact two-stamp line for the ledger card: when detected vs when it happened.
export const TimelineInline = ({ obs, testId }) => {
  const detected = obs?.first_detected_at;
  const observed = obs?.provenance?.observed_at;
  const status = obs?.event_status || "measured";
  const m = eventMeta(obs);
  const lag = status === "occurred" ? latencySeconds(observed, detected) : null;
  return (
    <div className="mt-2 flex items-center gap-3 text-[9px] font-mono text-atlas-secondary/85" data-testid={testId}>
      {detected && (
        <span className="inline-flex items-center gap-1" title={`Detected by Vigilo Earth · ${fmtAbs(detected)}`}>
          <Radar className="w-3 h-3 text-atlas-cyan/70" strokeWidth={2} />
          detected {timeAgo(detected)}
        </span>
      )}
      <span className={`inline-flex items-center gap-1 ${m.color}`} title={observed ? fmtAbs(observed) : ""}>
        <m.Icon className="w-3 h-3" strokeWidth={2} />
        {status === "watch" ? "not confirmed" : `${m.label.toLowerCase()} ${timeAgo(observed) || "—"}`}
      </span>
      {lag != null && (
        <span className={`inline-flex items-center gap-1 ${latencyTone(lag)}`} title="Detection latency — event occurred → Vigilo Earth flagged it">
          <Timer className="w-3 h-3" strokeWidth={2} />
          caught {fmtDuration(lag)} later
        </span>
      )}
    </div>
  );
};

const Stamp = ({ Icon, label, value, sub, color }) => (
  <div className="flex items-start gap-2">
    <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${color}`} strokeWidth={2} />
    <div className="min-w-0">
      <div className="text-[8px] font-mono uppercase tracking-widest text-atlas-secondary/80">{label}</div>
      <div className={`text-[12px] font-display leading-tight ${color}`}>{value}</div>
      <div className="text-[9px] font-mono text-atlas-secondary/80 leading-snug">{sub}</div>
    </div>
  </div>
);

// Full event timeline for the detail drawer.
export const TimelineBlock = ({ obs, testId }) => {
  const detected = obs?.first_detected_at;
  const observed = obs?.provenance?.observed_at;
  const status = obs?.event_status || "measured";
  const m = eventMeta(obs);
  const lag = status !== "watch" ? latencySeconds(observed, detected) : null;
  return (
    <div className="mb-4 border border-white/10 bg-white/[0.02] p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-2.5">
        <Clock className="w-3.5 h-3.5 text-atlas-cyan" />
        <span className="text-[10px] uppercase tracking-widest text-atlas-cyan/70 font-mono">Event Timeline</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Stamp
          Icon={Radar}
          label="Detected by Vigilo Earth"
          value={detected ? timeAgo(detected) : "—"}
          sub={detected ? fmtAbs(detected) : "not flagged as an anomaly"}
          color="text-atlas-cyan"
        />
        <Stamp
          Icon={m.Icon}
          label={status === "watch" ? "Actual event" : m.label}
          value={status === "watch" ? "Not confirmed" : timeAgo(observed) || "—"}
          sub={
            status === "watch"
              ? `Watch — risk from conditions as of ${observed ? fmtAbs(observed) : "—"}`
              : observed
              ? fmtAbs(observed)
              : "—"
          }
          color={m.color}
        />
      </div>
      {lag != null && (
        <div
          className="mt-2.5 pt-2.5 border-t border-white/5 flex items-center justify-between"
          data-testid={testId ? `${testId}-latency` : undefined}
        >
          <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-atlas-secondary/80">
            <Timer className="w-3.5 h-3.5" /> Detection latency
          </span>
          <span className={`text-[12px] font-mono ${latencyTone(lag)}`}>
            {fmtDuration(lag)}{" "}
            <span className="text-atlas-secondary/75 normal-case">
              {status === "occurred" ? "after it occurred" : "after measurement"}
            </span>
          </span>
        </div>
      )}
    </div>
  );
};
